
import { Image as FabricImage, IText as FabricIText, Circle as FabricCircle, Rect as FabricRect, Ellipse as FabricEllipse, Polygon as FabricPolygon, Pattern as FabricPattern } from 'fabric';
import { requestAIWithCache } from '../utils/requestAIWithCache';
import { enableTextWarping, disableTextWarping } from '../components/textWarp_copy';
import { enable3dEffect, disable3dEffect } from '../components/text3d';
import { enableSplineWarp, disableSplineWarp } from '../components/textSpline';
import { enableDotsEffect, disableDotsEffect } from '../components/textDots';
import { enableBezierWarp, disableBezierWarp } from '../components/textBezier';
import { enableMultiColor, disableMultiColor } from '../components/textMultiColor';
import { enableCircularWarp, disableCircularWarp } from '../components/textCircular';
import { enableLiquidWarp, disableLiquidWarp } from '../components/textLiquid';
import { moveToIndex } from '../utils/imageUtils';



export async function applyPatternFill(shape: any, imageUrl: string, repeat: string = 'no-repeat'): Promise<void> {
    console.log('applyPatternFill/......', shape.ai)
    const imgEl = document.createElement('img');
    imgEl.crossOrigin = 'anonymous';
    let finalUrl = imageUrl.includes('?') ? imageUrl : imageUrl + '?x-oss-process=style/large';
    if (shape.ai) {
        let res = await requestAIWithCache(JSON.stringify({ code: shape.ai, url: imageUrl }));
        if (res.status == "completed") {
            finalUrl = (res.src || '') + '?x-oss-process=style/large';
        }
    }
    return new Promise<void>((resolve) => {
        imgEl.onload = () => {
            // 使用图片原始宽高作为基础，避免从 shape 获取可能不正确的尺寸
            const w = imgEl.naturalWidth || imgEl.width || 1;
            const h = imgEl.naturalHeight || imgEl.height || 1;
            const tile = document.createElement('canvas');
            tile.width = Math.max(1, Math.round(w));
            tile.height = Math.max(1, Math.round(h));
            const ctx = tile.getContext('2d');
            if (ctx) {
                const iw = w;
                const ih = h;
                // 1:1 绘制原始图片到 tile
                const s = 1;
                const dw = iw * s;
                const dh = ih * s;
                const dx = (tile.width - dw) / 2;
                const dy = (tile.height - dh) / 2;
                ctx.imageSmoothingEnabled = true;
                // @ts-ignore
                ctx.imageSmoothingQuality = 'high';
                ctx.clearRect(0, 0, tile.width, tile.height);
                ctx.drawImage(imgEl, dx, dy, dw, dh);
            }

            const patternSource = {
                source: tile,
                repeat: repeat
            };

            const pattern = new (FabricPattern as any)(patternSource);

            // 计算 patternTransform 以适应 shape (Cover 模式)
            const shapeWidth = typeof shape.getScaledWidth === 'function' ? shape.getScaledWidth() : ((shape.width || 1) * (shape.scaleX || 1));
            const shapeHeight = typeof shape.getScaledHeight === 'function' ? shape.getScaledHeight() : ((shape.height || 1) * (shape.scaleY || 1));

            // 计算需要的缩放比例以覆盖 shape (short 模式：短边覆盖，即取 max)
            const scaleX = shapeWidth / w;
            const scaleY = shapeHeight / h;
            const scale = Math.max(scaleX, scaleY);

            // 需要抵消 shape 自身的缩放 (obj.scaleX/Y) 并应用新的缩放 (scale)
            const objScaleX = shape.scaleX || 1;
            const objScaleY = shape.scaleY || 1;

            const finalScaleX = scale / objScaleX;
            const finalScaleY = scale / objScaleY;

            // 设置 patternTransform: [scaleX, skewY, skewX, scaleY, translateX, translateY]
            (pattern as any).patternTransform = [
                finalScaleX, 0, 0, finalScaleY,
                0, 0
            ];

            shape.set({ fill: pattern });
            // 更新 data 属性以便持久化
            const existingData = shape.data || {};
            shape.set('data', { ...existingData, fillSrc: finalUrl, patternRepeat: repeat });
            shape.setCoords();
            resolve();
        };
        imgEl.onerror = () => {
            console.warn('Failed to load pattern image:', finalUrl);
            resolve(); // 即使失败也 resolve，避免挂起
        };
        imgEl.src = finalUrl;
    });
}

export function getEffectInfo(fabricSelection: any) {
    let effectType = 'none';
    if (!fabricSelection) return {
        effectType,
    };

    // Determine effect type
    if (fabricSelection.effectType && fabricSelection.effectType !== 'none') {
        effectType = fabricSelection.effectType;
    } else if (fabricSelection.effect === 'extrude') {
        effectType = 'extrude';
    } else if (fabricSelection.effect === 'dots') {
        effectType = 'dots';
    } else if (fabricSelection.splineGridSize) {
        effectType = 'spline';
    } else if (fabricSelection.gridSize) {
        effectType = 'warp';
    } else if (fabricSelection.isBezierWarped) {
        effectType = 'bezier';
    } else if (fabricSelection.isMultiColorEffect) {
        effectType = 'multicolor';
    } else if (fabricSelection.isCircularWarped) {
        effectType = 'circular';
    } else if (fabricSelection.isLiquidWarped) {
        effectType = 'liquidwave';
    } else if (fabricSelection.is3dDotsEffect) {
        effectType = '3ddot';
    }

    // Populate effect data based on type
    let effectData = null;
    if (effectType === 'extrude') {
        effectData = { effectOptions: fabricSelection.effectOptions };
    } else if (effectType === 'dots') {
        effectData = { dotSize: fabricSelection.dotSize };
    } else if (effectType === 'spline') {
        effectData = { splineGridSize: fabricSelection.splineGridSize, srcPts: fabricSelection.srcPts, dstPts: fabricSelection.dstPts };
    } else if (effectType === 'warp') {
        effectData = { gridSize: fabricSelection.gridSize, gridPoints: fabricSelection.gridPoints };
    } else if (effectType === 'bezier') {
        effectData = { bezierControls: fabricSelection.bezierControls, baselineOffset: fabricSelection.baselineOffset };
    } else if (effectType === 'multicolor') {
        effectData = { multiColorPoints: fabricSelection.multiColorPoints };
    } else if (effectType === 'circular') {
        effectData = { circularWarpOptions: fabricSelection.circularWarpOptions };
    } else if (effectType === 'liquidwave') {
        effectData = { liquidWarpOptions: fabricSelection.liquidWarpOptions };
    } else if (effectType === '3ddot') {
        effectData = { threeDDotOptions: fabricSelection.threeDDotOptions };
    }

    return {
        effectType,
        effectData
    };
}

export async function createFabricObjectFromLayer(base: any, layer: any, canvasW: number = 0, canvasH: number = 0): Promise<any> {
    const lw = layer.width || 0;
    const lh = layer.height || 0;
    let shape: any = null;
    if (layer.type.toLowerCase() === 'itext') {
        shape = new FabricIText(layer.text || '', {
            ...base,
            fontSize: layer.fontSize || 16,
            fontFamily: layer.fontFamily || 'Arial',
        } as any);
        if (layer.effectType && layer.effectType !== 'none') {
            console.log("layer.effectType", layer.effectType);
            // Disable all effects first to ensure a clean state
            disableTextWarping(shape);
            disable3dEffect(shape);
            disableSplineWarp(shape);
            disableDotsEffect(shape);
            disableBezierWarp(shape);
            disableMultiColor(shape);
            disableCircularWarp(shape);
            disableLiquidWarp(shape);

            const effectData = layer.effectData || {};
            // Enable the new effect
            if (layer.effectType === 'warp') {
                enableTextWarping(shape, effectData.gridSize || 3, effectData.gridPoints || []);
            } if (layer.effectType === 'extrude') {
                enable3dEffect(shape, effectData.threeDDotOptions || {});
            } else if (layer.effectType === 'spline') {
                enableSplineWarp(shape, effectData.splineGridSize || 3, effectData.srcPts || [], effectData.dstPts || []);
            } else if (layer.effectType === 'dots') {
                enableDotsEffect(shape, effectData.dotSize || 3);
            } else if (layer.effectType === 'bezier') {
                enableBezierWarp(shape, effectData.bezierControls || {}, effectData.baselineOffset);
            } else if (layer.effectType === 'multicolor') {
                enableMultiColor(shape);
            } else if (layer.effectType === 'circular') {
                const options = effectData.circularWarpOptions || {};
                enableCircularWarp(shape, {
                    radius: options.radius || layer.circularRadius,
                    rotations: options.rotations || layer.circularRotations,
                    rotationOffset: options.rotationOffset || layer.circularAngle
                });
            } else if (layer.effectType === 'liquidwave') {
                const options = effectData.liquidWarpOptions || {};
                enableLiquidWarp(shape, {
                    radius: options.radius || layer.liquidRadius,
                    flowStrength: options.flowStrength || layer.liquidStrength,
                    noiseStrength: options.noiseStrength || layer.liquidAngle
                });
            }
            console.log(shape)
        }
    }
    if (layer.type.toLowerCase() === 'rect') {
        shape = new FabricRect({ ...base } as any);
    }
    if (layer.type.toLowerCase() === 'circle') {
        shape = new FabricCircle({ ...base, radius: lw / 2 } as any);
    }
    if (layer.type.toLowerCase() === 'ellipse' || layer.type.toLowerCase() === 'ellipse') {
        const rx = lw / 2;
        const ry = lh / 2;
        shape = new FabricEllipse({
            left: base.left,
            top: base.top,
            originX: 'center',
            originY: 'center',
            rx,
            ry,
            angle: base.angle,
            fill: base.fill,
            stroke: base.stroke,
            strokeWidth: base.strokeWidth,
            scaleX: base.scaleX,
            scaleY: base.scaleY,
            visible: base.visible,
        } as any);
    }
    if (layer.type === 'Polygon') {
        const points = layer.points || [];
        shape = new FabricPolygon(points as any, {
            left: base.left,
            top: base.top,
            originX: 'center',
            originY: 'center',
            angle: base.angle,
            fill: base.fill,
            stroke: base.stroke,
            strokeWidth: base.strokeWidth,
            scaleX: base.scaleX,
            scaleY: base.scaleY,
            visible: base.visible,
        } as any);
    }
    if (shape && typeof layer.fill === 'object' && layer.fill.src) {
        if (layer.ai) {
            shape.ai = layer.ai;
        }
        await applyPatternFill(shape, layer.fill.src, layer.fill.patternRepeat || 'no-repeat');
    }
    return shape;
}

// 将单个设计图层渲染到 Fabric.js 画布
export async function renderLayerOnFabricCanvas(fabricCanvas: any, layer: any, index: number): Promise<boolean> {
    try {
        const existLayer = fabricCanvas.getObjects()[index];
        const canvasW = typeof fabricCanvas.getWidth === 'function' ? fabricCanvas.getWidth() : (fabricCanvas.width || 0);
        const canvasH = typeof fabricCanvas.getHeight === 'function' ? fabricCanvas.getHeight() : (fabricCanvas.height || 0);

        const lw = layer.width || 0;
        const lh = layer.height || 0;
        const base = {
            id: layer.id,
            left: canvasW * (layer.options?.left || 0) || 0,
            top: canvasH * (layer.options?.top || 0) || 0,
            width: lw,
            height: lh,
            originX: 'center',
            originY: 'center',
            scaleX: (layer.options?.width || 0) * canvasW / lw || 1,
            scaleY: (layer.options?.height || 0) * canvasH / lh || 1,
            angle: typeof layer.angle === 'number' ? layer.angle : 0,
            visible: true,
            fill: layer.fill,
            stroke: layer.stroke || '#000000',
            strokeWidth: layer.strokeWidth || 0
        } as any;
        if (layer.type.toLowerCase() === 'image' && layer.src) {
            base.src = layer.src;
            let url = (layer.src || '') + '?x-oss-process=style/large';
            if (layer.ai) {
                try {
                    console.log('get ai image',layer.src)
                    base.ai = layer.ai;
                    let res = await requestAIWithCache(JSON.stringify({ code: layer.ai, url: layer.src }));
                    console.log('get ai res',res)
                    if (res.status == "completed") {
                        url = (res.src || '') + '?x-oss-process=style/large';
                    }
                } catch (e) { console.error(e) }
            }
            console.log(url)
            const img = await (FabricImage as any).fromURL(url, { crossOrigin: 'anonymous' } as any);
            if (img) {
                if (existLayer) {
                    fabricCanvas.remove(existLayer);
                    const sX = existLayer.getScaledWidth ? existLayer.getScaledWidth() / lw : 1;
                    const sY = existLayer.getScaledHeight ? existLayer.getScaledHeight() / lh : 1;
                    img.set({ id: layer.id, src: layer.src, ai: layer.ai, left: base.left, top: base.top, originX: base.originX, originY: base.originY, scaleX: sX, scaleY: sY } as any);
                    
                } else
                    img.set({ ...base });
                fabricCanvas.add(img);
                if (existLayer) {
                    moveToIndex(fabricCanvas, img, index);
                }
                return true;
            }
        } else if (layer.type.toLowerCase() === 'circle' || layer.type.toLowerCase() === 'rect' || layer.type.toLowerCase() === 'polygon' || layer.type.toLowerCase() === 'itext') {
            const shape = await createFabricObjectFromLayer(base, layer, fabricCanvas.width, fabricCanvas.height);
            if (shape) {
                fabricCanvas.add(shape);
                return true;
            }
        }
        console.log("holding.....")
        return false;
    } catch (e) {
        console.warn('renderLayerOnFabricCanvas error', e);
        return false;
    }
}
